// skill.md — what the workspace has told us about how it sounds.
//
// These files go into every draft whole, in the order they were last edited,
// up to a budget. Nothing is chunked, embedded or retrieved: a voice guide is
// a few kilobytes against a context window measured in millions of tokens, and
// returning three paragraphs of a document that already fits is strictly worse
// than sending the document (docs/AI_STUDIO_PLAN.md §6).

import { useEffect, useRef, useState } from "react";
import { deleteSkill, listSkills, saveSkill } from "./api";
import { isPaywall, paywallMessage } from "../lib/entitlements";
import { Loading, Spinner } from "../components/Spinner";
import { Link } from "react-router-dom";
import type { AiSkill, DraftIntent } from "../lib/types";

const SCOPES: Array<{ key: "all" | DraftIntent; label: string }> = [
  { key: "all", label: "Everything" },
  { key: "post", label: "Posts only" },
  { key: "description", label: "Descriptions only" },
  { key: "punchline", label: "Punch lines only" },
];

const EXAMPLE = `# How we sound

We are a physiotherapy clinic, not a gym. Warm, plain, never hyped.

- Say "session", not "workout".
- Never promise a recovery time.
- One idea per post. No lists of benefits.
- Our founder is Sam; mention them by first name only.
`;

export function Skills({ workspaceId, canEdit }: { workspaceId: string; canEdit: boolean }) {
  const [skills, setSkills] = useState<AiSkill[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paywall, setPaywall] = useState<string | null>(null);

  // The one being written. Null means the editor is closed, which is different
  // from an empty draft — a closed editor is not an unsaved one.
  const [editing, setEditing] = useState<Partial<AiSkill> | null>(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    listSkills(workspaceId)
      .then(setSkills)
      .catch((e) => setError(e.message ?? String(e)));
  }, [workspaceId]);

  function refresh() {
    return listSkills(workspaceId).then(setSkills).catch(() => { /* the list is already on screen */ });
  }

  async function submit() {
    if (!editing?.name?.trim() || !editing.body_md?.trim()) return;
    setBusy(true);
    setError(null);
    setPaywall(null);
    try {
      await saveSkill({
        id: editing.id,
        workspaceId,
        name: editing.name,
        body: editing.body_md,
        appliesTo: (editing.applies_to as "all" | DraftIntent) ?? "all",
        isEnabled: editing.is_enabled ?? true,
      });
      setEditing(null);
      await refresh();
    } catch (e) {
      // The file limit is a plan boundary, and it arrives from a trigger as a
      // PT402 the same way an over-size upload does.
      if (isPaywall(e)) setPaywall(paywallMessage(e));
      else setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function toggle(skill: AiSkill) {
    setToggling(skill.id);
    try {
      await saveSkill({
        id: skill.id,
        workspaceId,
        name: skill.name,
        body: skill.body_md,
        appliesTo: skill.applies_to,
        isEnabled: !skill.is_enabled,
      });
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setToggling(null);
    }
  }

  async function remove(skill: AiSkill) {
    setRemoving(skill.id);
    try {
      await deleteSkill(skill.id);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRemoving(null);
    }
  }

  /** A .md dropped in is the same thing as one pasted in. */
  async function readFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setEditing({
      name: f.name.replace(/\.(md|markdown|txt)$/i, ""),
      body_md: text,
      applies_to: "all",
      is_enabled: true,
    });
    e.target.value = "";
  }

  return (
    <div className="card">
      <h2>Brand knowledge</h2>
      <p className="muted">
        Markdown files describing how you sound, what you sell, and what you never say. Every
        enabled file goes into every draft, so this is the difference between copy that could be
        anyone's and copy that is yours.
      </p>

      {error && <div className="err">{error}</div>}
      {paywall && (
        <div className="notice upgrade">{paywall} <Link to="/plans">See plans</Link></div>
      )}

      {!skills ? (
        <Loading label="Loading your brand knowledge…" />
      ) : skills.length === 0 && !editing ? (
        <p className="muted">Nothing here yet.</p>
      ) : (
        <ul className="skill-list">
          {(skills ?? []).map((s) => (
            <li key={s.id} className={s.is_enabled ? "" : "off"}>
              <div className="skill-head">
                <strong>{s.name}</strong>
                <span className={s.is_enabled ? "chip ok" : "chip"}>
                  {s.is_enabled ? "In use" : "Off"}
                </span>
                <span className="muted">
                  {SCOPES.find((x) => x.key === s.applies_to)?.label}
                </span>
                <span className="spacer" />
                {canEdit && (
                  <>
                    <button onClick={() => setEditing(s)}>Edit</button>
                    <button onClick={() => toggle(s)} disabled={toggling === s.id}>
                      {toggling === s.id && <Spinner />}{" "}
                      {s.is_enabled ? "Turn off" : "Turn on"}
                    </button>
                    <button className="danger" onClick={() => remove(s)} disabled={removing === s.id}>
                      {removing === s.id ? "Removing…" : "Delete"}
                    </button>
                  </>
                )}
              </div>
              <pre className="skill-preview">{s.body_md.slice(0, 400)}</pre>
            </li>
          ))}
        </ul>
      )}

      {canEdit && !editing && (
        <div className="row" style={{ marginTop: "1rem" }}>
          <button onClick={() => setEditing({
            name: "", body_md: EXAMPLE, applies_to: "all", is_enabled: true,
          })}>
            Write one
          </button>
          <input ref={fileInput} type="file" accept=".md,.markdown,.txt,text/markdown,text/plain"
                 onChange={readFile} className="visually-hidden" tabIndex={-1} />
          <button onClick={() => fileInput.current?.click()}>Upload a .md file</button>
        </div>
      )}

      {editing && (
        <div className="skill-editor">
          <label htmlFor="skill-name">Name</label>
          <input id="skill-name" value={editing.name ?? ""}
                 placeholder="Voice"
                 onChange={(e) => setEditing({ ...editing, name: e.target.value })} />

          <label htmlFor="skill-scope">Use it for</label>
          <select id="skill-scope" value={editing.applies_to ?? "all"}
                  onChange={(e) => setEditing({ ...editing, applies_to: e.target.value as "all" | DraftIntent })}>
            {SCOPES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>

          <label htmlFor="skill-body">The file</label>
          <textarea id="skill-body" rows={14} className="mono"
                    value={editing.body_md ?? ""}
                    onChange={(e) => setEditing({ ...editing, body_md: e.target.value })} />

          <div className="row" style={{ marginTop: ".8rem" }}>
            <button className="primary" onClick={submit}
                    disabled={busy || !editing.name?.trim() || !editing.body_md?.trim()}>
              {busy && <Spinner />} {busy ? "Saving…" : "Save"}
            </button>
            <button onClick={() => setEditing(null)} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
